import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CPSearch from "../components/CPSearch";
import { Sparkles, MapPin, MessageCircle, Wand2, CreditCard, CheckCircle2, ArrowRight, Store } from "lucide-react";
import { api, formatMXN } from "../lib/api";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/c2afb0df-2913-4bfa-9056-e084c30908bf/images/9aa9548337ac4a3dda3f9e8694e4749cd322346f976a12e03c86410cc7e92714.png";
const AI_SHOWCASE = "https://static.prod-images.emergentagent.com/jobs/c2afb0df-2913-4bfa-9056-e084c30908bf/images/651fa8c002f6d48f979726ea709860c09cae1f04217b5249a2e8b2cea59ea13b.png";
const STORE_IMGS = [
  "https://images.pexels.com/photos/28153646/pexels-photo-28153646.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/16837824/pexels-photo-16837824.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/19638986/pexels-photo-19638986.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/18981487/pexels-photo-18981487.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];

export default function BusinessAffiliate() {
  const [directories, setDirectories] = useState([]);
  const [params] = useSearchParams();
  const pid = params.get("pid");
  const registroLink = pid ? `/registro?pid=${encodeURIComponent(pid)}` : "/registro";

  useEffect(() => {
    api.get("/directory/list").then((r) => setDirectories(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-in-up">
            <div className="overline text-[hsl(var(--primary))] inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))] animate-pulse" />
              Para comercios · Afíliate
            </div>
            <h1 className="font-display text-5xl sm:text-6xl leading-[1.02] tracking-tight mt-4">
              Tu negocio en el mejor programa de <span className="bg-gradient-to-r from-[#7C3AED] to-[#5B21B6] bg-clip-text text-transparent">LEALTAD</span>, clientes felices = mayores ventas.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
              OFERTAS permite que tu negocio entre a la moda de dar <strong>PUNTOS/RIFAS</strong> por compra. Actualmente empresas como <strong>SORIANA</strong>, <strong>LIVERPOOL</strong>, <strong>CHEDRAUI</strong> entre otras más saben de los beneficios comerciales que brindan estos sistemas.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to={registroLink} className="btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3 font-medium" data-testid="affiliate-cta-register">
                Anuncia mi comercio <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#como-funciona" className="text-sm font-medium hover:text-[hsl(var(--primary))]">
                Ver cómo funciona →
              </a>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              <Stat n="$199" label="MXN al mes" />
              <Stat n="1" label="anuncio diseñado por IA" />
              <Stat n="∞" label="vecinos cerca de ti" />
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden border border-border bg-white shadow-xl">
              <img src={HERO_IMG} alt="Comercios mexicanos exitosos" className="w-full h-[520px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0E0E12]/40 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur rounded-2xl p-4 border border-white/60">
                <div className="overline text-[hsl(var(--primary))]">Ahora en CDMX</div>
                <div className="font-display text-lg leading-tight mt-1">
                  +{directories.length || 0} colonias activas. ¿Y la tuya?
                </div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:block bg-white rounded-2xl p-4 shadow-lg border border-border w-56">
              <div className="overline text-muted-foreground">Promo del día</div>
              <div className="discount-stamp inline-block mt-1 px-2 py-1 rounded text-xs font-bold">2x1 TACOS</div>
              <div className="text-xs text-muted-foreground mt-2">Vista 248 veces hoy</div>
            </div>
          </div>
        </div>

        <div aria-hidden className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#7C3AED] opacity-[0.18] blur-3xl" />
        <div aria-hidden className="absolute top-40 -left-32 w-96 h-96 rounded-full bg-[#A78BFA] opacity-[0.18] blur-3xl" />
      </section>

      {/* SOCIAL PROOF — STORES */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        <div className="overline text-muted-foreground mb-4">Comercios que ya están dentro</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 stagger">
          {STORE_IMGS.map((src, i) => (
            <div key={src} className="aspect-[4/3] rounded-xl overflow-hidden border border-border">
              <img src={src} alt={`comercio-${i}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="como-funciona" className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-2 gap-12 items-start">
          <div>
            <div className="overline text-[hsl(var(--primary))]">Cómo funciona</div>
            <h2 className="font-display text-4xl sm:text-5xl mt-3 leading-[1.05] tracking-tight">
              Tres pasos para estar en el directorio de tu colonia.
            </h2>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground">
              Sin diseñador, sin contratos, sin complicaciones. Un pago al mes y tu anuncio listo para que tus vecinos lo encuentren.
            </p>
          </div>
          <div className="space-y-6 stagger">
            <Step n="01" icon={<Store className="w-5 h-5" />} title="Registra tu comercio" body="Captura tu nombre, giro, CP y WhatsApp. Toma 2 minutos." />
            <Step n="02" icon={<CreditCard className="w-5 h-5" />} title="Paga $199 MXN por transferencia" body="Datos bancarios BBVA. Subes tu comprobante y te activamos 30 días." />
            <Step n="03" icon={<Wand2 className="w-5 h-5" />} title="Crea tu anuncio con IA" body="Escribes tu promoción, la IA diseña el cartel. Lo refinas hasta amarlo y se publica en tu directorio CP." />
          </div>
        </div>
      </section>

      {/* AI SHOWCASE */}
      <section className="gradient-dark text-white border-y border-border relative overflow-hidden">
        <div aria-hidden className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[#7C3AED] opacity-30 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-12 items-center">
          <div className="rounded-3xl overflow-hidden border border-white/10 bg-white/5 glow-purple">
            <img src={AI_SHOWCASE} alt="IA diseñando anuncio" className="w-full h-[460px] object-cover" />
          </div>
          <div>
            <div className="overline text-[#C4B5FD]">Inteligencia artificial · Gemini</div>
            <h2 className="font-display text-4xl sm:text-5xl mt-3 leading-tight tracking-tight">
              Una IA diseñadora <span className="text-[#A78BFA]">solo para tu negocio</span>.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-zinc-300">
              Describe tu promo en una línea ("2x1 en quesadillas los miércoles") y la IA genera un cartel publicitario profesional. Si no te gusta algo —cambia colores, tipografía, fondo— solo escríbelo y la IA lo ajusta.
            </p>
            <ul className="mt-6 space-y-3">
              <Bullet>Diseños cuadrados ideales para directorio y redes</Bullet>
              <Bullet>Un anuncio nuevo por mes + refinamientos ilimitados</Bullet>
              <Bullet>Sube tu logo y la IA lo respeta en el cartel</Bullet>
              <Bullet>Solo imágenes — sin videos, todo claro y enfocado</Bullet>
            </ul>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="precios" className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <div className="overline text-[hsl(var(--primary))]">Un solo plan, sin sorpresas</div>
          <h2 className="font-display text-4xl sm:text-5xl mt-3 tracking-tight">Anúnciate por {formatMXN(199)} al mes.</h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground">
            Pago exclusivo por transferencia bancaria. Activación dentro de las 24h tras revisar tu comprobante.
          </p>
        </div>

        <div className="mt-12 max-w-2xl mx-auto bg-white rounded-3xl border border-border p-10 relative overflow-hidden">
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-[hsl(var(--primary))]/10" />
          <div className="relative">
            <div className="flex items-baseline gap-2">
              <div className="font-display text-6xl">$199</div>
              <div className="text-muted-foreground">MXN / mes</div>
            </div>
            <ul className="mt-8 space-y-3">
              <Bullet>1 anuncio diseñado con IA / mes</Bullet>
              <Bullet>Refinamientos ilimitados del diseño</Bullet>
              <Bullet>Publicación en el directorio de tu CP</Bullet>
              <Bullet>Contacto directo por WhatsApp con tus clientes</Bullet>
              <Bullet>Panel personalizado para administrar tu anuncio</Bullet>
              <Bullet>Con tu código QR abren tu menú o mini web</Bullet>
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={registroLink} className="btn-primary rounded-full px-6 py-3 font-medium" data-testid="pricing-cta-register">
                Empezar ahora
              </Link>
              <Link to="/login" className="rounded-full px-6 py-3 border border-border hover:bg-muted">
                Ya tengo cuenta
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* LOYALTY + RAFFLE — exclusive to OFERTAS */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="loyalty-raffle-section-affiliate">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Loyalty card */}
          <div className="rounded-3xl bg-gradient-to-br from-[#7C3AED] via-[#6D28D9] to-[#4C1D95] text-white p-8 md:p-10 relative overflow-hidden">
            <div className="absolute -top-12 -right-12 w-64 h-64 rounded-full bg-amber-300/20 blur-3xl pointer-events-none" />
            <div className="overline text-amber-300">SUMA CLIENTES CAUTIVOS</div>
            <h3 className="font-display text-3xl sm:text-4xl tracking-tight mt-2 leading-[1.05]">
              Activa el <span className="text-amber-300">programa de lealtad</span>
            </h3>
            <p className="text-white/85 mt-3 text-base sm:text-lg max-w-md">
              Tus clientes acumulan puntos por cada compra. Tú decides el premio: un refresco gratis, una hamburguesa sin costo, un kilo de carne, un café — lo que se ajuste a tu negocio.
            </p>
            <ul className="mt-5 space-y-2 text-base text-white/90">
              <li className="flex items-start gap-2"><span className="text-amber-300 font-bold mt-0.5">✓</span> Tú defines puntos por compra y el premio.</li>
              <li className="flex items-start gap-2"><span className="text-amber-300 font-bold mt-0.5">✓</span> Escaneas el QR del cliente y le das sus puntos en 1 segundo.</li>
              <li className="flex items-start gap-2"><span className="text-amber-300 font-bold mt-0.5">✓</span> Más clientes vuelven a tu local por su premio.</li>
            </ul>
          </div>

          {/* Raffle card */}
          <div className="rounded-3xl bg-gradient-to-br from-amber-300 via-amber-400 to-amber-600 text-amber-950 p-8 md:p-10 relative overflow-hidden">
            <div className="absolute -bottom-12 -left-12 w-64 h-64 rounded-full bg-white/20 blur-3xl pointer-events-none" />
            <div className="overline">REGALO DE OFERTAS MÉXICO · GRATIS PARA TI</div>
            <h3 className="font-display text-3xl sm:text-4xl tracking-tight mt-2 leading-[1.05]">
              Rifa mensual de <span className="text-[#5B21B6]">electrodomésticos</span>
            </h3>
            <p className="mt-3 text-base sm:text-lg max-w-md">
              <strong>Por cada compra en tu local, el cliente recibe un boleto digital</strong> para una rifa mensual de electrodomésticos que organiza OFERTAS México — sin costo para ti.
            </p>
            <ul className="mt-5 space-y-2 text-base">
              <li className="flex items-start gap-2"><span className="text-[#5B21B6] font-bold mt-0.5">✓</span> Argumento de venta para tus clientes — "compra aquí y participa".</li>
              <li className="flex items-start gap-2"><span className="text-[#5B21B6] font-bold mt-0.5">✓</span> Tráfico recurrente todos los meses.</li>
              <li className="flex items-start gap-2"><span className="text-[#5B21B6] font-bold mt-0.5">✓</span> Refrigeradores, pantallas, microondas… diferente cada mes.</li>
            </ul>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const Stat = ({ n, label }) => (
  <div>
    <div className="font-display text-3xl">{n}</div>
    <div className="text-xs text-muted-foreground mt-1">{label}</div>
  </div>
);

const Step = ({ n, icon, title, body }) => (
  <div className="flex gap-4 items-start bg-white rounded-2xl border border-border p-5">
    <div className="overline text-muted-foreground pt-1">{n}</div>
    <div className="w-10 h-10 rounded-lg bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] grid place-items-center shrink-0">
      {icon}
    </div>
    <div>
      <div className="font-display text-xl">{title}</div>
      <p className="text-base text-muted-foreground mt-1">{body}</p>
    </div>
  </div>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-2 text-base">
    <CheckCircle2 className="w-4 h-4 text-[#7C3AED] mt-0.5 shrink-0" />
    <span>{children}</span>
  </li>
);
